import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectorRef, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  buildHeaderDataShell,
  firstGridRowObjectFromPayload,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../../shared/workspace-element-context';
import { Config861EditService } from './config-861-edit.service';

@Component({
    selector: 'gtw-config-861',
    templateUrl: './config-861-edit.component.html',
    styleUrls: ['./config-861-edit.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class Config861EditComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private readonly subs = new Subscription();

  // Standard component properties 
  currentTabName = "Form Entry";
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  modalTitle = "Edit Config";

  // Global service data
  globalService: any = {};
  USER_SETTINGS: any = {};
  GENERAL_CONFIG: any = {};
  workspaceFactory: any = {};

  // Component data
  rowData: any = {};
  filterParams: any = {};
  baseUrls: any = {};

  // Form data
  taxYear: string = '';
  configuration: any = null;
  configGIEAppr: any = null;
  configGIEApprtionData: any[] = [];
  configInfo: any[] = [];
  configInfoTemp: any[] = [];
  saved861ConfigData: any[] = [];
  isCreate: boolean = false;
  scenario: string = '';
  jcd_key: any = '';
  filterTaxYear: string = '';

  private config861EditService: Config861EditService;

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef,
    private http: HttpClient
  ) {
    this.config861EditService = new Config861EditService(this.http);
  }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseUrls = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header.globalParams || {};
    this.USER_SETTINGS = header.userSettingsData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = firstGridRowObjectFromPayload(rowPayloadFromActiveScreen(activeScreen));
  }

  init() {
    try {
      if (this.filterParams && Object.keys(this.filterParams).length > 0) {
        
        // Set basic data
        this.filterTaxYear = this.filterParams.tax_year;
        this.scenario = this.filterParams.scenario;
        this.jcd_key = this.globalService.jcd_key;
        
        // Determine if this is CREATE or EDIT mode
        // In AngularJS: vm.isCreate = (gridData.length == 0) ? true : false;
        // gridData.length == 0 means ADD mode, otherwise EDIT mode
        this.isCreate = !this.rowData || Object.keys(this.rowData).length === 0 || !this.rowData.ADJ_KEY;
        
        this.taxYear = this.isCreate ? this.filterTaxYear : this.rowData.TAX_YEAR;
        
        // Set modal title based on mode
        this.modalTitle = this.isCreate ? "Add Config" : "Edit Config";

        this.getDefaultDropDown();
        this.getSaved861Config();
      }
    } catch (e) {
      console.error('Error in Config861Edit init:', e);
    }
  }

  getDefaultDropDown() {
    const params = {
      action_code: 'tcanfc',
      tax_year: this.filterTaxYear,
      scenario: this.scenario
    };

    this.config861EditService.getCodeMasterData(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.jsonObject) {
          this.configInfoTemp = response.jsonObject.filter((data: any) => 
            data.CODE_GROUP === '861_CONFIG' || data.CODE_GROUP === 'YES_NO'
          );
          
          this.configGIEApprtionData = response.jsonObject.filter((data: any) => 
            data.CODE_GROUP === '861_GIE_CONFIG' || data.CODE_GROUP === '861_GIE_CONFIG_NEW'
          );

          if (!this.isCreate) {
            // Find and set GIE Apportionment FIRST
            for (let item of this.configGIEApprtionData) {
              if (item.CODE_DESCRIPTION === this.rowData.GIE_DESC && 
                  parseInt(item.CODE_SHORT_DESC) === parseInt(this.rowData.GIE_KEY)) {
                this.configGIEAppr = item;
                break;
              }
            }
            
            // Call this to populate configInfo array
            this.apprConfigDropdownDetails();
            
            // THEN set configuration after configInfo is populated
            this.configuration = {
              CODE_KEY: this.rowData.CONFIG_KEY,
              CODE_DESCRIPTION: this.rowData.CONFIG_DESC
            };
          }
        }
      },
      (error: any) => {
        console.error('Error loading code master data:', error);
        this._snackBar.open('Error loading dropdown data', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  getSaved861Config() {
    const params = {
      action_code: 'bb3f1t',
      tax_year: this.filterTaxYear,
      scenario: this.scenario
    };

    this.config861EditService.getCodeMasterData(
      this.GENERAL_CONFIG.base_url,
      params,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        if (response && response.jsonObject) {
          this.saved861ConfigData = response.jsonObject;
        }
      },
      (error: any) => {
        console.error('Error loading saved 861 config data:', error);
      }
    );
  }

  apprConfigDropdownDetails() {
    if (this.configGIEAppr && this.configGIEAppr.CODE_GROUP === "861_GIE_CONFIG") {
      this.configInfo = this.configInfoTemp.filter((data: any) => 
        data.CODE_GROUP === '861_CONFIG'
      );
    } else if (this.configGIEAppr && this.configGIEAppr.CODE_GROUP === "861_GIE_CONFIG_NEW") {
      this.configInfo = this.configInfoTemp.filter((data: any) => 
        data.CODE_GROUP === 'YES_NO'
      );
    } else {
      // Clear configInfo if no valid apportionment selected
      this.configInfo = [];
    }
  }

  onApprChange() {
    this.apprConfigDropdownDetails();
    this.configuration = null; // Reset configuration when apportionment changes
  }

  save() {
    // Validation
    const currentYear = new Date().getFullYear();
    if ((currentYear - 1) < parseInt(this.taxYear)) {
      this._snackBar.open(`For ${this.taxYear} tax year config can not be created`, 'Close', {
        duration: 4000,
        panelClass: ['warning-snackbar']
      });
      return;
    }

    if (!this.configuration || !this.configuration.CODE_KEY) {
      this._snackBar.open('Configuration is required', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (!this.configGIEAppr || !this.configGIEAppr.CODE_KEY) {
      this._snackBar.open('GIE/Apportionment is required', 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    // Check for duplicate configuration
    if (!this.isCreate) {
      for (let item of this.saved861ConfigData) {
        if (item.GIE_KEY == this.configGIEAppr.CODE_SHORT_DESC && 
            item.ADJ_KEY != this.rowData.ADJ_KEY) {
          this._snackBar.open('Configuration already exists for the selected Apportionment.', 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
          return;
        }
      }
    }

    this.isSaveClicked = true;
    this.crudLoading = true;

    const returnObj = {
      sso_id: this.USER_SETTINGS.user?.sso_id,
      tax_year: this.taxYear,
      scenario: this.scenario,
      jcd_key: this.globalService.jcd_key,
      operation_type: this.isCreate ? 'I' : 'U',
      config_desc: this.configuration.CODE_DESCRIPTION,
      config_key: this.configuration.CODE_KEY,
      gie_desc: this.configGIEAppr.CODE_DESCRIPTION,
      gie_key: this.configGIEAppr.CODE_SHORT_DESC
    };

    const clobDataDetails = [returnObj];

    this.config861EditService.saveConfig861(
      this.GENERAL_CONFIG.base_url,
      clobDataDetails,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('861 Configuration has been saved/updated successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
      }
    );
  }

  reset(type?: string) {
    // AngularJS reset logic: if(type == 'add') vs else for edit
    if (this.isCreate || type === 'add') {
      this.configuration = null;
      this.configGIEAppr = null;
    } else {
      this.getDefaultDropDown();
    }
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  // Track by function for ngFor to improve performance
  trackByCodeKey(index: number, item: any): any {
    return item ? item.CODE_KEY : undefined;
  }

  // Compare function for select options to properly match objects by CODE_KEY
  compareConfigOptions(option1: any, option2: any): boolean {
    // Handle null/undefined values
    if (option1 == null && option2 == null) return true;
    if (option1 == null || option2 == null) return false;
    
    // Compare objects by CODE_KEY
    return option1 && option2 ? option1.CODE_KEY === option2.CODE_KEY : option1 === option2;
  }
}
